import {
  DropdownSubmitButton,
  Form,
  Loading,
  MonetaryInputField,
  SelectField,
  SubmitButton,
  TextAreaInputField,
} from '@/components';
import {
  useAccountTitleOptions,
  useAccountingType,
  useAccountingTypeOptions,
  useAccountingTypes,
} from '@/features/master-table/hooks';
import { useProject } from '@/features/project/_hooks/project';
import { ExpenditureCartFormSchema } from '@/features/project/projects/_models/expenditure-cart';
import { useForm, useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { usePreviewRowState } from '../shared/PreviewRowStateProvider';

type FormValues = z.infer<typeof ExpenditureCartFormSchema>;
type CreateExpenditureCartNextDrawerKey =
  | 'expenditure-cart'
  | 'expenditure-item';

type Props = {
  fiscalYear: number | undefined;
  isSkipAdministrativeReformTeamEvaluation?: boolean;
  initialValues?: Partial<FormValues>;
  onSubmit: ({
    values,
    nextDrawerKey,
    onSuccess,
  }: {
    values: FormValues;
    nextDrawerKey?: CreateExpenditureCartNextDrawerKey;
    onSuccess?: () => void;
  }) => Promise<void>;
  isEditing?: boolean;
  type?: 'basic' | 'review-skipped';
};

export const ExpenditureCartForm: FC<Props> = ({
  fiscalYear,
  isSkipAdministrativeReformTeamEvaluation,
  initialValues,
  onSubmit,
  isEditing,
  type = 'basic',
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    watch,
    resetField,
    reset,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ExpenditureCartFormSchema),
  });
  const { id } = useId();
  const project = useProject(id);
  const { setPreviewRowState } = usePreviewRowState();

  const accountingId = watch('accountingId');
  const accountingType = useAccountingType({ id: accountingId });
  const accountingTypeOptions = useAccountingTypeOptions({
    params: { referenceFiscalYear: fiscalYear?.toString() },
    enabled: !!fiscalYear,
  });
  const accountTitleOptions = useAccountTitleOptions({
    accountingId,
    params: { referenceFiscalYear: fiscalYear?.toString() },
    enabled: !!fiscalYear,
  });
  const accountingTypes = useAccountingTypes({
    params: { referenceFiscalYear: fiscalYear?.toString() },
    enabled: !!fiscalYear,
  });
  const { unit } = useLocaledAmount();

  if (accountingTypeOptions.isPending || !accountingTypeOptions.data) {
    return <Loading />;
  }

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(async (values: FormValues) => {
        await onSubmit({
          values,
        });
      })}
    >
      <SelectField
        name="accountingId"
        label={t('accounting-name')}
        control={control}
        options={accountingTypeOptions.data}
        errors={errors}
        disabled={type === 'review-skipped'}
        required
        shouldCoexistOnChange
        onChange={(value) => {
          resetField('accountTitleId', {
            defaultValue: null,
          });
          setPreviewRowState({
            type: 'expenditure-cart',
            accountingType: accountingTypes.data?.find((t) => t.id === value),
          });
        }}
        useNotInOption
      />
      <SelectField
        name="accountTitleId"
        label={t('account-title')}
        control={control}
        errors={errors}
        options={accountTitleOptions.data}
        disabled={
          accountingType.data?.type !== 'special' ||
          !accountTitleOptions.data?.length ||
          type === 'review-skipped'
        }
        useNotInOption
      />
      <MonetaryInputField
        name="desiredAmount"
        control={control}
        label={t('desired-amount')}
        hint={
          isSkipAdministrativeReformTeamEvaluation
            ? undefined
            : t('hint-desired-amount')
        }
        errors={errors}
        unit={unit}
        disabled={type === 'review-skipped'}
      />
      <TextAreaInputField
        name="remark"
        label={t('remark')}
        hint={t('hint-expenditure-cart-remark')}
        control={control}
        errors={errors}
        rows={1}
      />
      {isEditing ? (
        <SubmitButton
          loading={isSubmitting}
          disabled={!isDirty && type === 'basic'}
        />
      ) : (
        <DropdownSubmitButton<CreateExpenditureCartNextDrawerKey>
          loading={isSubmitting}
          menuItems={[
            {
              key: 'expenditure-item',
              label: t('save-and-create-item', {
                item: project.data?.majorExpenditureBudgetPurposeId
                  ? `${t('budget-type')}/${t('expenditure-purpose-level-3-1')}`
                  : `${t('budget-type')}/${t('expenditure-budget-purpose')}`,
              }),
            },
            {
              key: 'expenditure-cart',
              label: t('save-and-create-next-item', {
                item: t('accounting-name'),
              }),
            },
          ]}
          onClick={(e) => {
            handleSubmit(async (values) => {
              await onSubmit({
                values,
                nextDrawerKey: e.key,
                onSuccess: () => {
                  if (e.key === 'expenditure-cart') {
                    reset(initialValues);
                  }
                },
              });
            })();
          }}
        />
      )}
    </Form>
  );
};
